import React, { useEffect, useState } from "react";
import { Alert, Box, Button, MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { fetchPromoStoreEmployees } from "../../../../../services/promoStoreAddons";
import PromoStoreEmployeeImport from "./PromoStoreEmployeeImport";
import PromoStoreEmployeeReport from "./PromoStoreEmployeeReport";

export const employeeActivationLabel = (record) => record.activated ? "Activated" : "Not activated";
const message = (error) => error?.response?.data?.message || "Could not read employee records from WordPress.";

export default function PromoStoreEmployeeDirectory({ storeId, instanceUniqueId, isActive, users = [], refreshKey = 0, onChange, onViewUser, canImport = false }) {
  const scope = `${instanceUniqueId}:${storeId}`;
  const ids = [...new Set(users.map((user) => Number(user.wpUserId)).filter((id) => Number.isSafeInteger(id) && id > 0))].sort((a, b) => a - b).join(",");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState({ search: "", status: "all", page: 1 });
  const [revision, setRevision] = useState(0);
  const [directory, setDirectory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [links, setLinks] = useState({});
  const [linksLoading, setLinksLoading] = useState(false);
  const [linksError, setLinksError] = useState("");

  useEffect(() => {
    setDirectory(null); setSearch(""); setQuery({ search: "", status: "all", page: 1 });
  }, [scope]);

  useEffect(() => {
    let cancelled = false;
    setError("");
    if (!isActive || !storeId || !instanceUniqueId) { setLoading(false); return undefined; }
    setLoading(true);
    fetchPromoStoreEmployees(storeId, instanceUniqueId, { ...query, perPage: 25 })
      .then((result) => { if (!cancelled) setDirectory({ ...result, scope }); })
      .catch((failure) => { if (!cancelled) { setDirectory(null); setError(message(failure)); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [storeId, instanceUniqueId, scope, isActive, query, revision, refreshKey]);

  const enabled = isActive && directory?.scope === scope && directory.enabled === true;
  useEffect(() => {
    let cancelled = false;
    setLinks({}); setLinksError(""); setLinksLoading(false);
    if (!isActive || !enabled || !ids) return undefined;
    setLinksLoading(true);
    const load = async () => {
      const allIds = ids.split(",");
      const next = {};
      for (let index = 0; index < allIds.length; index += 100) {
        const result = await fetchPromoStoreEmployees(storeId, instanceUniqueId, { userIds: allIds.slice(index, index + 100).join(",") });
        if (cancelled) return;
        if (!result.enabled) { setDirectory(null); return; }
        Object.assign(next, result.userLinks);
      }
      if (!cancelled) setLinks(next);
    };
    load().catch((failure) => { if (!cancelled) setLinksError(message(failure)); })
      .finally(() => { if (!cancelled) setLinksLoading(false); });
    return () => { cancelled = true; };
  }, [enabled, ids, storeId, instanceUniqueId, isActive, revision, refreshKey]);

  useEffect(() => {
    onChange?.({ scope, enabled, links, loading: linksLoading, error: linksError });
  }, [scope, enabled, links, linksLoading, linksError, onChange]);

  if (!isActive || (!enabled && !error)) return null;
  return (
    <Box sx={{ mt: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6">Employee roster</Typography>
        <Button disabled={loading} onClick={() => setRevision((value) => value + 1)}>Refresh roster</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Typography role="status" sx={{ mb: 1 }}>Loading employee roster…</Typography>}
      {linksError && <Alert severity="error" sx={{ mb: 2 }}>{linksError} User employee links are unavailable until refreshed.</Alert>}
      {enabled && <>
        <PromoStoreEmployeeReport key={scope} storeId={storeId} instanceUniqueId={instanceUniqueId} refreshKey={`${refreshKey}:${revision}`} />
        {canImport && <PromoStoreEmployeeImport storeId={storeId} instanceUniqueId={instanceUniqueId} onComplete={() => setRevision((value) => value + 1)} />}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Uploaded employee records, including people without an activated account. Activation reflects the roster’s account claim; account email verification is managed by WordPress.</Typography>
        <Stack component="form" direction={{ xs: "column", sm: "row" }} spacing={1} onSubmit={(event) => { event.preventDefault(); setQuery((value) => ({ ...value, search, page: 1 })); }} sx={{ mb: 2 }}>
          <TextField label="Search employee ID or name" size="small" value={search} inputProps={{ maxLength: 191 }} onChange={(event) => setSearch(event.target.value)} />
          <TextField label="Roster status" size="small" select value={query.status} onChange={(event) => setQuery((value) => ({ ...value, status: event.target.value, page: 1 }))} sx={{ minWidth: 170 }}>
            <MenuItem value="all">All employees</MenuItem><MenuItem value="activated">Activated</MenuItem><MenuItem value="not_activated">Not activated</MenuItem><MenuItem value="disabled">Disabled</MenuItem>
          </TextField>
          <Button type="submit" disabled={loading}>Search roster</Button>
        </Stack>
        <TableContainer>
          <Table size="small" aria-label="Employee roster">
            <TableHead><TableRow>{["Employee ID", "Name", "Activation", "Record status", "WP account", "Claimed"].map((label) => <TableCell key={label}>{label}</TableCell>)}</TableRow></TableHead>
            <TableBody>{directory.records.map((record) => {
              const user = users.find((entry) => Number(entry.wpUserId) === record.wpUserId);
              return <TableRow key={record.id}>
                <TableCell>{record.employeeId}</TableCell><TableCell>{record.name || "—"}</TableCell>
                <TableCell>{employeeActivationLabel(record)}</TableCell><TableCell>{record.status}</TableCell>
                <TableCell>{user ? <Button size="small" onClick={() => onViewUser?.(user)}>View user {record.wpUserId}</Button> : record.wpUserId || "No claimed account"}</TableCell>
                <TableCell>{record.claimedAt || "—"}</TableCell>
              </TableRow>;
            })}</TableBody>
          </Table>
        </TableContainer>
        {!directory.records.length && <Typography sx={{ p: 2 }}>No employees match this filter.</Typography>}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
          <Button disabled={loading || query.page === 1} onClick={() => setQuery((value) => ({ ...value, page: value.page - 1 }))}>Previous</Button>
          <Typography variant="body2">Page {query.page} · {directory.total} employees</Typography>
          <Button disabled={loading || query.page * 25 >= directory.total} onClick={() => setQuery((value) => ({ ...value, page: value.page + 1 }))}>Next</Button>
        </Stack>
      </>}
    </Box>
  );
}
